import { useCustomScrollbar } from 'src/hooks/use-custom-visible-scrollbar/useCustomVisibleScrollbar';
import { ToolOptionsProps } from './ToolOptionsRenderer.types';
import { useVerticalScrollView } from 'src/hooks/useVerticalScrollView';
import { useCallback, useEffect, useState } from 'react';
import { mergeRefs } from 'src/helpers/mergeRefs.helper';
import { ToolOptionsRendererProps } from '../../BaseToolbar.types';
import { useStore } from 'src/contexts/StoreContext';
import { DYNAMIC_MODAL_GROUPS } from 'src/store/toast/toastStore.types';
import { getToolOptionTitleI18nKey } from 'src/_shared/utils/getI18nKeys';
import { useHorizontalScrollView } from 'src/editor-v3/hooks/useHorizontalScrollView';

export const useToolOptionsRendererLogic = (
  { onSelectToolOption, isActive, options, isV3 }: ToolOptionsRendererProps<ToolOptionsProps>,
  customIsOptionSelectedValidation?: (option: ToolOptionsProps) => boolean,
) => {
  const scrollViewRef = useCustomScrollbar();
  const { scrollVierRef, getScrollViewElementTestId, scrollToIndex } = !isV3
    ? useVerticalScrollView()
    : useHorizontalScrollView();
  const { i18n, toastStore } = useStore();

  const [selectedProductOptionChoiceCode, setSelectedProductOptionChoiceCode] = useState(() => {
    const selectedOption = options.find((option: ToolOptionsProps) => option?.selected);
    return selectedOption ? selectedOption?.productOptionChoiceCode : null;
  });
  const selectedChoice = options.find(option => option?.selected);
  useEffect(() => {
    if (!selectedChoice) {
      setSelectedProductOptionChoiceCode(null);
    } else if (selectedChoice.productOptionChoiceCode !== selectedProductOptionChoiceCode) {
      setSelectedProductOptionChoiceCode(selectedChoice.productOptionChoiceCode);
    }
  }, [selectedChoice]);

  const isOptionSelected = useCallback(
    (option: ToolOptionsProps) => {
      const isSelected =
        option?.productOptionChoiceCode === selectedProductOptionChoiceCode &&
        (!customIsOptionSelectedValidation || customIsOptionSelectedValidation(option));
      return isSelected;
    },
    [selectedProductOptionChoiceCode, customIsOptionSelectedValidation],
  );

  useEffect(() => {
    if (isActive) {
      const activeToolIndex = options.findIndex((option: ToolOptionsProps) => isOptionSelected(option));
      if (activeToolIndex >= 0) {
        scrollToIndex(activeToolIndex);
      }
    }
  }, [isActive, options]);

  const onPressAndSelectToolOption = async (option: ToolOptionsProps) => {
    setSelectedProductOptionChoiceCode(option.productOptionChoiceCode);
    onSelectToolOption && onSelectToolOption(option);
    toastStore.showToast({
      message: i18n.t('toast:product-option-applied-successfully', {
        option: i18n.t(getToolOptionTitleI18nKey(option.productOptionCode)),
      }),
      duration: 3000,
      id: `apply-product-option-${option.productOptionChoiceCode}`,
      group: DYNAMIC_MODAL_GROUPS.ApplyProductOption,
    });
  };

  return {
    scrollViewRef: mergeRefs(scrollVierRef, scrollViewRef),
    getScrollViewElementTestId,
    isOptionSelected,
    onPressAndSelectToolOption,
  };
};
